/**
 * PackageDetailView — the standalone "all about THIS network" detail page
 * (NETWORK-REGISTRY.md §9), used by both the Apps and Review tabs. NOT a row in a
 * network list — the whole surface is the one selected network.
 *
 * Three panels:
 *   • LEFT (nav): network name + ASL Comment + purpose, then clickable sections —
 *     Agents · Skills · MCP · Connectors (+ the default-workspace note).
 *   • MIDDLE: the selected item's detail (agent role+prompt / skill SKILL.md /
 *     connector or MCP info incl. credential requirement).
 *   • RIGHT: the read-only ASL diagram (reuses GraphVisualization; selecting an
 *     agent highlights its node).
 *
 * Fetches the deep package detail itself (``getRegistryPackageDetail``) so the host
 * page just hands it a package ref. Read-only — no live network, no edit paths.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GraphVisualization from './GraphVisualization';
import { aslToReactFlow, isASL } from '@/utils/aslToReactFlow';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import NetworkAvatar from '@stepflow/ui-shared/components/dashboard/NetworkAvatar.jsx';
import { getRegistryPackageDetail } from '@stepflow/ui-shared/services/networkRegistryApi.js';
import { listMcpRegistry } from '@stepflow/ui-shared/services/toolsApi.js';

// A middle-panel selection: { kind: 'agent'|'skill'|'connector'|'mcp', id }
function NavItem({ label, active, sub, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-xs ${
        active ? 'bg-blue-50 font-medium text-blue-800' : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      <span className="truncate">{label}</span>
      {sub ? <span className="shrink-0 text-[10px] text-gray-400">{sub}</span> : null}
    </button>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="mb-1 mt-3 px-2.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
      {children}
    </div>
  );
}

export default function PackageDetailView({ pkg }) {
  const registryNetworkId = String(pkg?.registryNetworkId || '');
  const version = Number(pkg?.version) || undefined;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mcpMeta, setMcpMeta] = useState({});
  // Middle-panel selection.
  const [sel, setSel] = useState(null); // { kind, id }

  // Draggable split between MIDDLE (text) and RIGHT (diagram). `diagramPct` is
  // the diagram's share of the two-panel width; clamped so neither collapses.
  const DIAGRAM_MIN = 20;
  const DIAGRAM_MAX = 75;
  const [diagramPct, setDiagramPct] = useState(40);
  const [dragging, setDragging] = useState(false);
  const splitRef = useRef(null); // wraps MIDDLE + handle + RIGHT

  const onSplitDrag = useCallback((clientX) => {
    const el = splitRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const pct = ((rect.right - clientX) / rect.width) * 100;
    setDiagramPct(Math.min(DIAGRAM_MAX, Math.max(DIAGRAM_MIN, pct)));
  }, []);

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => { e.preventDefault(); onSplitDrag(e.clientX); };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    // Suppress text selection while dragging.
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = prev;
    };
  }, [dragging, onSplitDrag]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setSel(null);
    getRegistryPackageDetail(registryNetworkId, version)
      .then((d) => { if (alive) setDetail(d || {}); })
      .catch((e) => { if (alive) setError(e?.message || 'Failed to load'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [registryNetworkId, version]);

  const { comment = '', purpose = '', asl = null, agents = [], skills = [], toolsUsed = [] } = detail || {};
  const connectors = useMemo(() => toolsUsed.filter((t) => t.kind === 'connector'), [toolsUsed]);
  const mcps = useMemo(() => toolsUsed.filter((t) => t.kind === 'mcp'), [toolsUsed]);
  const hasBuilder = useMemo(
    () => agents.some((a) => String(a.type || '').toUpperCase() === 'UNIVERSAL_BUILDER'),
    [agents],
  );
  const graph = useMemo(() => (isASL(asl) ? aslToReactFlow(asl) : null), [asl]);

  const agentById = useMemo(() => Object.fromEntries(agents.map((a) => [a.logicalId, a])), [agents]);
  const skillById = useMemo(() => Object.fromEntries(skills.map((s) => [s.skillId, s])), [skills]);

  // Fetch MCP registry metadata when the package uses MCPs (for their detail).
  useEffect(() => {
    if (!mcps.length) return undefined;
    let alive = true;
    listMcpRegistry()
      .then((res) => {
        if (!alive) return;
        const m = {};
        for (const r of (res?.mcps || [])) { m[r.id] = r; if (r.displayName) m[r.displayName] = r; }
        setMcpMeta(m);
      })
      .catch(() => { if (alive) setMcpMeta({}); });
    return () => { alive = false; };
  }, [mcps.length]);

  if (loading) {
    return <div className="flex h-full items-center justify-center text-xs text-gray-500">Loading…</div>;
  }
  if (error) {
    return <div className="flex h-full items-center justify-center text-xs text-red-600">{error}</div>;
  }

  const highlightLogicalId = sel?.kind === 'agent' ? sel.id : null;

  return (
    <div className="flex h-full min-h-0">
      {/* LEFT — network identity + section nav */}
      <div className="flex w-64 shrink-0 flex-col overflow-y-auto border-r border-gray-100 bg-gray-50/40 py-3">
        <div className="px-3">
          <div className="flex items-start gap-2">
            {/* The package's identity avatar — hexagon (registry), seeded by the
                portable visualSeed so it matches the tile + every deployment. */}
            <NetworkAvatar
              networkId={registryNetworkId}
              name={pkg?.title}
              seed={pkg?.visualSeed || registryNetworkId}
              shape="hexagon"
              size={28}
              className="mt-0.5"
            />
            <div className="min-w-0">
              {/* Title = home: clicking clears the selection → back to the Overview. */}
              <button type="button" onClick={() => setSel(null)}
                data-testid="pkg-title"
                title="Back to overview"
                className="text-left text-sm font-semibold text-gray-900 hover:text-blue-700">
                {pkg?.title || registryNetworkId}
              </button>
              {pkg?.stepflowVersion ? (
                <div className="mt-0.5 text-[10px] text-gray-400">{pkg.stepflowVersion} · {pkg.domain || 'General'}</div>
              ) : null}
            </div>
          </div>
          {purpose ? (
            <p className="mt-2 text-[11px] italic leading-relaxed text-gray-500">{purpose}</p>
          ) : null}
          {hasBuilder ? (
            <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-2 py-1 text-[10px] text-emerald-900"
              data-testid="pkg-default-workspace">
              Deploys with a ready-to-use workspace{skills.length ? ` (${skills.length} skills baked in)` : ''}.
            </div>
          ) : null}
        </div>

        <SectionHeader>Agents ({agents.length})</SectionHeader>
        {agents.map((a) => (
          <div className="px-1.5" key={a.logicalId}>
            <NavItem label={a.logicalId} sub={a.modelId} testid={`pkg-agent-${a.logicalId}`}
              active={sel?.kind === 'agent' && sel.id === a.logicalId}
              onClick={() => setSel({ kind: 'agent', id: a.logicalId })} />
          </div>
        ))}

        <SectionHeader>Skills ({skills.length})</SectionHeader>
        {skills.length === 0 ? <div className="px-3 text-[11px] text-gray-400">None</div> : null}
        {skills.map((s) => (
          <div className="px-1.5" key={s.skillId}>
            <NavItem label={s.skillId} testid={`pkg-skill-${s.skillId}`}
              active={sel?.kind === 'skill' && sel.id === s.skillId}
              onClick={() => setSel({ kind: 'skill', id: s.skillId })} />
          </div>
        ))}

        <SectionHeader>MCP ({mcps.length})</SectionHeader>
        {mcps.length === 0 ? <div className="px-3 text-[11px] text-gray-400">None</div> : null}
        {mcps.map((t, i) => (
          <div className="px-1.5" key={`m${i}`}>
            <NavItem label={(mcpMeta[t.name]?.displayName) || t.name} testid={`pkg-mcp-${t.name}`}
              sub={t.scope === 'network' ? 'network' : (t.agentId || 'agent')}
              active={sel?.kind === 'mcp' && sel.id === `${t.name}#${i}`}
              onClick={() => setSel({ kind: 'mcp', id: `${t.name}#${i}`, name: t.name })} />
          </div>
        ))}

        <SectionHeader>Connectors ({connectors.length})</SectionHeader>
        {connectors.length === 0 ? <div className="px-3 text-[11px] text-gray-400">None</div> : null}
        {connectors.map((t, i) => (
          <div className="px-1.5" key={`c${i}`}>
            <NavItem label={t.name} testid={`pkg-conn-${t.name}`}
              sub={t.scope === 'network' ? 'network' : (t.agentId || 'agent')}
              active={sel?.kind === 'connector' && sel.id === `${t.name}#${i}`}
              onClick={() => setSel({ kind: 'connector', id: `${t.name}#${i}`, name: t.name })} />
          </div>
        ))}
      </div>

      {/* MIDDLE + RIGHT share a resizable split (drag handle between them). */}
      <div ref={splitRef} className="flex min-w-0 flex-1">
      {/* MIDDLE — the selected item's detail */}
      <div className="min-w-0 flex-1 overflow-y-auto px-5 py-4 text-xs text-gray-800" data-testid="pkg-middle">
        {!sel ? (
          <div className="space-y-2" data-testid="pkg-overview">
            <h3 className="text-sm font-semibold text-gray-900">Overview</h3>
            {comment ? (
              <p className="whitespace-pre-wrap leading-relaxed text-gray-700">{comment}</p>
            ) : (
              <p className="text-gray-400">No description provided.</p>
            )}
            <p className="pt-1 text-[11px] text-gray-400">
              Select an agent, skill, MCP, or connector on the left for its details.
            </p>
          </div>
        ) : sel.kind === 'agent' ? (
          (() => {
            const a = agentById[sel.id] || {};
            return (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">{a.logicalId}</h3>
                {a.role ? <div><span className="font-semibold text-gray-500">Role:</span> {a.role}</div> : null}
                {a.modelId ? <div><span className="font-semibold text-gray-500">Model:</span> {a.modelId}</div> : null}
                {Array.isArray(a.tools) && a.tools.length ? (
                  <div><span className="font-semibold text-gray-500">Tools:</span> {a.tools.join(', ')}</div>
                ) : null}
                {a.systemPrompt ? (
                  <div>
                    <div className="font-semibold text-gray-500">Prompt</div>
                    <pre className="mt-1 whitespace-pre-wrap rounded bg-gray-50 p-2 text-[11px] text-gray-800 ring-1 ring-gray-200">{a.systemPrompt}</pre>
                  </div>
                ) : null}
              </div>
            );
          })()
        ) : sel.kind === 'skill' ? (
          <MarkdownRenderer content={skillById[sel.id]?.skillMd || '_No content._'} />
        ) : sel.kind === 'mcp' ? (
          (() => {
            const meta = mcpMeta[sel.name];
            const needsCred = meta ? meta.authRequired : undefined;
            return (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">{meta?.displayName || sel.name}</h3>
                <div className="text-[11px] uppercase tracking-wide text-gray-400">MCP server</div>
                {meta?.description ? <p>{meta.description}</p> : <p className="text-gray-400">Details unavailable in this workspace.</p>}
                {needsCred === true ? (
                  <p><span className="font-semibold text-amber-700">Credential required</span>
                    {Array.isArray(meta?.credentialFields) && meta.credentialFields.length
                      ? <> — {meta.credentialFields.map((f) => f.label || f.key).join(', ')}</> : null}
                    <> (entered at deploy).</></p>
                ) : needsCred === false ? (
                  <p><span className="font-semibold text-emerald-700">No credential needed.</span></p>
                ) : null}
              </div>
            );
          })()
        ) : sel.name === 'workspace' ? (
          // The "workspace" entry is a room-context workspace pin, NOT a credentialed
          // API connector — the deploy seed re-provisions it, so no credential is asked.
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Workspace</h3>
            <div className="text-[11px] uppercase tracking-wide text-gray-400">Room workspace</div>
            <p>A ready-to-use room workspace this network's rooms run in. It's provisioned automatically when you deploy.</p>
            <p><span className="font-semibold text-emerald-700">No credential needed.</span></p>
          </div>
        ) : (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">{sel.name}</h3>
            <div className="text-[11px] uppercase tracking-wide text-gray-400">Connector</div>
            <p>A code/API connector this network calls out through.</p>
            <p><span className="font-semibold text-amber-700">Credential required</span> — provided when you deploy (Connections step).</p>
          </div>
        )}
      </div>

      {/* Drag handle — resize MIDDLE vs RIGHT. Hidden below lg (diagram hidden). */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize diagram panel"
        data-testid="pkg-split-handle"
        onMouseDown={(e) => { e.preventDefault(); setDragging(true); }}
        onDoubleClick={() => setDiagramPct(40)}
        title="Drag to resize · double-click to reset"
        className={`hidden w-1.5 shrink-0 cursor-col-resize border-l border-gray-100 transition-colors lg:block ${
          dragging ? 'bg-blue-300' : 'bg-gray-100 hover:bg-blue-200'
        }`}
      />
      {/* RIGHT — read-only ASL diagram (width driven by the drag handle) */}
      <aside className="hidden shrink-0 lg:block" style={{ width: `${diagramPct}%` }}>
        {graph ? (
          <GraphVisualization data={graph} readOnly highlightLogicalId={highlightLogicalId}
            stickyResetKey={registryNetworkId || 'pkg'} />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-gray-400">No diagram available.</div>
        )}
      </aside>
      </div>
    </div>
  );
}
